import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useUpdateWorkspaceSettings } from '../hooks/useWorkspaceMembers';
import { renameChange } from '../renameChange';
import { SettingsRow } from './SettingsRow';

export interface NameRowProps {
  workspaceId: string;
  name: string;
  /** Owners and org admins rename; everyone else reads the name. */
  canManage: boolean;
}

/** What the name is for, under the row's title. */
const LEAD = 'What this workspace is called everywhere it appears.';

/** Said to someone who may read the name but not change it. */
export const NAME_READ_ONLY_NOTE =
  'Only its owners and organization admins can rename it.';

/**
 * The workspace's name as the first setting of General (spec 227 FR-005): a
 * field that saves in place, where the band's pencil used to be. The band names
 * the page now, and the name stands beside it as a pill; a setting that is
 * changed belongs with the other settings, read like Visibility.
 *
 * Save and Cancel appear once the draft differs from the saved name, and are
 * buttons as well as keys, for a phone without either key. A refused save keeps
 * the text and says why beside the field. Whoever cannot rename reads the name
 * as text, with the note that says who can.
 */
export function NameRow({ workspaceId, name, canManage }: NameRowProps) {
  const update = useUpdateWorkspaceSettings();
  // `null` while nothing is being edited: the saved name is what shows, so a
  // rename made elsewhere arrives in the field rather than under a stale draft.
  const [draft, setDraft] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (!canManage) {
    return (
      <SettingsRow id="name-heading" title="Name" lead={LEAD}>
        <p className="text-foreground text-sm">{name}</p>
        <p className="text-muted-foreground text-xs">{NAME_READ_ONLY_NOTE}</p>
      </SettingsRow>
    );
  }

  const shown = draft ?? name;
  const change = draft === null ? null : renameChange(name, draft);
  const editing = draft !== null && draft !== name;
  const cancel = () => {
    setDraft(null);
    setRefusal(null);
  };
  const save = () => {
    if (change === null || update.isPending) return;
    setRefusal(null);
    update.mutate(
      { workspaceId, name: change },
      {
        onSuccess: () => {
          setDraft(null);
          toast.success(`Renamed to ${change}.`);
        },
        onError: (failure) => {
          setRefusal(failure.message);
          toast.error(failure.message);
        },
      },
    );
  };

  return (
    <SettingsRow id="name-heading" title="Name" lead={LEAD}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Workspace name"
          aria-invalid={refusal !== null ? true : undefined}
          aria-describedby={refusal !== null ? 'name-refusal' : undefined}
          value={shown}
          disabled={update.isPending}
          className="min-w-48 flex-1 sm:max-w-md"
          onChange={(event) => {
            setDraft(event.target.value);
            setRefusal(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              save();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              cancel();
            }
          }}
        />
        {editing ? (
          <span className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              disabled={change === null || update.isPending}
              onClick={save}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={update.isPending}
              onClick={cancel}
            >
              Cancel
            </Button>
          </span>
        ) : null}
      </div>
      {refusal !== null ? (
        <p id="name-refusal" role="alert" className="text-destructive text-xs">
          {refusal}
        </p>
      ) : null}
    </SettingsRow>
  );
}
